import type { FC } from 'react'
import type { CreateRewardPackageDTO, RewardPackageItem, Reward } from 'types/rewards'

import {
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material'

import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import { useCreateRewardPackage, invalidateRewardPackagesCache } from 'hooks/useRewardPackages'
import { useFetchCategoriesWithRewards } from 'hooks/useRewards'

import { useSnackbar } from 'notistack'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getRootOrganizationId } from 'types/user-profile'
import { PackageCategoryCard } from '../components/PackageCategoryCard'
import { ReviewCategoryCard } from '../components/ReviewCategoryCard'

interface RewardItemState {
  rewardId: string
  rewardName: string
  categoryName: string
  categoryDisplayOrder: number
  rewardDisplayOrder: number
  selected: boolean
  companyContribution: number
  employeeContribution: number
  supportsEmployeeContribution: boolean
}

const steps = ['Package Name', 'Select Rewards', 'Review']

const CreatePackage: FC = () => {
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')

  const [activeStep, setActiveStep] = useState(0)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [disclaimer, setDisclaimer] = useState('')
  const [rewardItems, setRewardItems] = useState<Map<string, RewardItemState>>(new Map())
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch categories with rewards
  const { categoriesWithRewards, isLoading: isLoadingRewards } = useFetchCategoriesWithRewards(
    rootOrganizationId,
    token!,
    !!rootOrganizationId && !!token
  )

  const { createPackage } = useCreateRewardPackage(token!)

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Build flat list of all rewards with category info
  const allRewards = useMemo(() => {
    if (!categoriesWithRewards) return []
    const rewards: (Reward & { categoryName: string; categoryDisplayOrder: number })[] = []
    categoriesWithRewards.forEach((cat) => {
      cat.rewards.forEach((reward) => {
        rewards.push({
          ...reward,
          categoryName: cat.name,
          categoryDisplayOrder: cat.displayOrder,
        })
      })
    })
    // Sort by category display order, then by reward display order
    rewards.sort((a, b) => {
      const catCompare = a.categoryDisplayOrder - b.categoryDisplayOrder
      if (catCompare !== 0) return catCompare
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    })
    return rewards
  }, [categoriesWithRewards])

  // Initialize reward items when rewards load
  useEffect(() => {
    if (allRewards.length > 0 && rewardItems.size === 0) {
      const items = new Map<string, RewardItemState>()
      allRewards.forEach((reward) => {
        items.set(reward.id!, {
          rewardId: reward.id!,
          rewardName: reward.name,
          categoryName: reward.categoryName,
          categoryDisplayOrder: reward.categoryDisplayOrder,
          rewardDisplayOrder: reward.displayOrder ?? 0,
          selected: false,
          companyContribution: 0,
          employeeContribution: 0,
          supportsEmployeeContribution: reward.supportsEmployeeContribution,
        })
      })
      setRewardItems(items)
    }
  }, [allRewards, rewardItems.size])

  const handleToggleReward = useCallback((rewardId: string) => {
    setRewardItems((prev) => {
      const next = new Map(prev)
      const item = next.get(rewardId)
      if (item) {
        next.set(rewardId, { ...item, selected: !item.selected })
      }
      return next
    })
  }, [])

  const handleContributionChange = useCallback(
    (rewardId: string, field: 'companyContribution' | 'employeeContribution', value: string) => {
      // Strip commas and parse the number
      const numericValue = parseFloat(value.replace(/,/g, '')) || 0
      setRewardItems((prev) => {
        const next = new Map(prev)
        const item = next.get(rewardId)
        if (item) {
          next.set(rewardId, {
            ...item,
            [field]: numericValue,
          })
        }
        return next
      })
    },
    []
  )

  const handleReorderRewards = useCallback((categoryName: string, oldIndex: number, newIndex: number) => {
    setRewardItems((prev) => {
      const next = new Map(prev)

      // Get all rewards in this category, sorted by display order
      const categoryRewards = Array.from(next.values())
        .filter((item) => item.categoryName === categoryName)
        .sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)

      // Move the item from oldIndex to newIndex
      const [movedItem] = categoryRewards.splice(oldIndex, 1)
      categoryRewards.splice(newIndex, 0, movedItem)

      // Update display orders for all rewards in this category
      categoryRewards.forEach((item, index) => {
        next.set(item.rewardId, {
          ...item,
          rewardDisplayOrder: index,
        })
      })

      return next
    })
  }, [])

  // Format number with commas
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  const handleToggleAll = useCallback((checked: boolean) => {
    setRewardItems((prev) => {
      const next = new Map(prev)
      next.forEach((item, key) => {
        next.set(key, { ...item, selected: checked })
      })
      return next
    })
  }, [])

  const handleNext = useCallback(() => {
    setActiveStep((prev) => prev + 1)
  }, [])

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1)
  }, [])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const selectedRewards: RewardPackageItem[] = []
      rewardItems.forEach((item) => {
        if (item.selected) {
          selectedRewards.push({
            rewardId: item.rewardId,
            displayOrder: item.rewardDisplayOrder,
            companyContribution: item.companyContribution,
            employeeContribution: item.employeeContribution,
          })
        }
      })

      const dto: CreateRewardPackageDTO = {
        rootOrganizationId,
        organizationId: rootOrganizationId,
        name,
        description: description.trim() || null,
        disclaimer: disclaimer.trim() || null,
        rewards: selectedRewards,
      }

      await createPackage(dto)
      enqueueSnackbar('Package created successfully', { variant: 'success' })
      invalidateRewardPackagesCache(rootOrganizationId, token!)
      startTransition(() => navigate('/rewards/packages'))
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create package'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get selected rewards for review
  const selectedRewards = useMemo(() => {
    const selected: RewardItemState[] = []
    rewardItems.forEach((item) => {
      if (item.selected) {
        selected.push(item)
      }
    })
    // Sort by category display order asc, then by reward display order asc
    selected.sort((a, b) => {
      const catCompare = a.categoryDisplayOrder - b.categoryDisplayOrder
      if (catCompare !== 0) return catCompare
      return a.rewardDisplayOrder - b.rewardDisplayOrder
    })
    return selected
  }, [rewardItems])

  // Calculate totals
  const totals = useMemo(() => {
    let company = 0
    let employee = 0
    selectedRewards.forEach((item) => {
      company += item.companyContribution
      employee += item.employeeContribution
    })
    return { company, employee, total: company + employee }
  }, [selectedRewards])

  // Group selected rewards by category for review step
  const selectedRewardsByCategory = useMemo(() => {
    const categories = new Map<
      string,
      { categoryName: string; categoryDisplayOrder: number; rewards: RewardItemState[] }
    >()

    selectedRewards.forEach((item) => {
      if (!categories.has(item.categoryName)) {
        categories.set(item.categoryName, {
          categoryName: item.categoryName,
          categoryDisplayOrder: item.categoryDisplayOrder,
          rewards: [],
        })
      }
      categories.get(item.categoryName)!.rewards.push(item)
    })

    // Sort rewards within each category by displayOrder
    categories.forEach((category) => {
      category.rewards.sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)
    })

    // Convert to array and sort by category display order
    return Array.from(categories.values()).sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)
  }, [selectedRewards])

  // Group rewards by category for card display
  const rewardsByCategory = useMemo(() => {
    const categories = new Map<
      string,
      { categoryName: string; categoryDisplayOrder: number; rewards: RewardItemState[] }
    >()

    allRewards.forEach((reward) => {
      const item = rewardItems.get(reward.id!)
      if (!item) return

      if (!categories.has(reward.categoryName)) {
        categories.set(reward.categoryName, {
          categoryName: reward.categoryName,
          categoryDisplayOrder: reward.categoryDisplayOrder,
          rewards: [],
        })
      }
      categories.get(reward.categoryName)!.rewards.push(item)
    })

    // Sort rewards within each category by displayOrder
    categories.forEach((category) => {
      category.rewards.sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)
    })

    // Convert to array and sort by category display order
    return Array.from(categories.values()).sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)
  }, [allRewards, rewardItems])

  const isNameValid = name.trim() !== ''
  const allSelected = rewardItems.size > 0 && Array.from(rewardItems.values()).every((item) => item.selected)
  const someSelected = Array.from(rewardItems.values()).some((item) => item.selected) && !allSelected

  const canProceed = () => {
    if (activeStep === 0) return isNameValid
    return true
  }

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 600, width: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Package Details
        </Typography>

        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Package Name"
          variant="outlined"
          pill
          shrink
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          name="description"
          label="Header Information"
          variant="outlined"
          pill
          shrink
          fullWidth
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of this rewards package (max 4 lines)"
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          name="disclaimer"
          label="Disclaimer"
          variant="outlined"
          pill
          shrink
          fullWidth
          multiline
          rows={3}
          value={disclaimer}
          onChange={(e) => setDisclaimer(e.target.value)}
          placeholder="Optional disclaimer text to display at the bottom of the rewards dashboard"
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
          Give your package a descriptive name. The header information will appear below the employee header on the
          rewards dashboard, and the disclaimer will appear at the bottom.
        </Typography>
      </Paper>
    </Box>
  )

  const renderStep2 = () => (
    <>
      {isLoadingRewards ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : allRewards.length === 0 ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography color="textSecondary" textAlign="center" py={4}>
            No rewards available. Create rewards first in Manage Rewards.
          </Typography>
        </Paper>
      ) : (
        <>
          {rewardsByCategory.map((category, index) => (
            <PackageCategoryCard
              key={category.categoryName}
              categoryName={category.categoryName}
              rewards={category.rewards}
              onToggleRewardSelect={handleToggleReward}
              onContributionChange={handleContributionChange}
              onReorderRewards={handleReorderRewards}
              formatCurrency={formatCurrency}
              theme={theme}
              showSelectAll={index === 0}
              allSelected={allSelected}
              someSelected={someSelected}
              onToggleAll={handleToggleAll}
            />
          ))}
        </>
      )}
    </>
  )

  const renderStep3 = () => (
    <>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <strong>Package Details</strong>
        </Typography>
        <Typography>
          <strong>Name:</strong> {name}
        </Typography>
        {description && (
          <Typography sx={{ mt: 1 }}>
            <strong>Header Information:</strong> {description}
          </Typography>
        )}
        {disclaimer && (
          <Typography sx={{ mt: 1 }}>
            <strong>Disclaimer:</strong> {disclaimer}
          </Typography>
        )}
        <Typography sx={{ mt: 1 }}>
          <strong>Selected Rewards:</strong> {selectedRewards.length}
        </Typography>
      </Paper>

      {selectedRewards.length === 0 ? (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography color="textSecondary" textAlign="center" py={2}>
            No rewards selected
          </Typography>
        </Paper>
      ) : (
        <>
          {selectedRewardsByCategory.map((category) => (
            <ReviewCategoryCard
              key={category.categoryName}
              categoryName={category.categoryName}
              rewards={category.rewards}
              theme={theme}
            />
          ))}

          <Paper sx={{ p: 2, mt: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} px={1.5}>
              <Box sx={{ flexGrow: 1, minWidth: 120 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  Totals
                </Typography>
              </Box>
              <Box sx={{ width: 140, textAlign: 'right', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  ${totals.company.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 140, textAlign: 'right', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  ${totals.employee.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </>
  )

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Create Package" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <AnimateButton>
            <Button
              variant="contained"
              color="warning"
              onClick={() => startTransition(() => navigate('/rewards/packages'))}
            >
              Cancel
            </Button>
          </AnimateButton>

          <Stack direction="row" alignItems="center" spacing={2}>
            {activeStep === 1 && (
              <Typography variant="body2" color="textSecondary">
                {selectedRewards.length} reward{selectedRewards.length !== 1 ? 's' : ''} selected
              </Typography>
            )}

            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <AnimateButton>
                <Button variant="contained" onClick={handleNext} disabled={!canProceed()}>
                  Next
                </Button>
              </AnimateButton>
            ) : (
              <AnimateButton>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isNameValid}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Package'}
                </Button>
              </AnimateButton>
            )}
          </Stack>
        </Stack>
      </Paper>
    </>
  )
}

export default CreatePackage
